/**
 * How a deployment's chargers reach the network (Warren, 2026-10-01):
 *
 *   "Install wizard and Submit a Location: add a required connectivity
 *   choice per deployment, 'Site WiFi or ethernet' or 'EV Initiative
 *   cellular', stored on each charger. Show it on the charger and
 *   deployment pages. No billing."
 *
 * Recorded once per deployment when it is registered
 * (NetworkSubmission.connectivity) and copied onto each unit when
 * commissioning creates it (Charger.connectivity, with connectivity_source
 * 'deployment' and connectivity_set_at). Deployments and chargers from
 * before the choice existed hold null and read as 'Not recorded'.
 *
 * Not to be confused with:
 *   - the per-session driver connection fee (Charger.connection_fee_cents,
 *     Fee type 'connection'): money, unrelated to the network link;
 *   - the SIM vendor (Charger.sim_provider): operator-asserted for the 1nce
 *     webhook and reconciliation flows. A unit on EV Initiative cellular may
 *     not have its SIM recorded yet, and nothing here sets sim_provider.
 * Nothing bills from this field.
 *
 * Warren, 2026-10-20: "There should be an option for both Site WiFi or
 * ethernet and EV Initiative cellular, not one or the other." The forms show
 * the two links as checkboxes, at least one required; ticking both stores
 * 'both' (a site with units on each, or units with both). A future SIM check
 * must use connectivityIncludes(v, 'evi_cellular'), never === 'evi_cellular'.
 */
export const CHARGER_CONNECTIVITY = ['site_network', 'evi_cellular', 'both'];
/** The two links a person ticks on the forms. */
export const CHARGER_CONNECTIVITY_LINKS = ['site_network', 'evi_cellular'];
export const CHARGER_CONNECTIVITY_LABELS = {
    site_network: 'Site WiFi or ethernet',
    evi_cellular: 'EV Initiative cellular',
    both: 'Site WiFi or ethernet and EV Initiative cellular',
};
/** One neutral hint line per choice on the registration forms. No price. */
export const CHARGER_CONNECTIVITY_HINTS = {
    site_network: 'Uses the site\'s own internet. No data charge from EV Initiative.',
    evi_cellular: 'Uses an EV Initiative SIM. Data is billed per top-up.',
    both: 'Uses the site\'s own internet and an EV Initiative SIM.',
};
/** What a deployment or charger without a recorded choice shows. */
export const CHARGER_CONNECTIVITY_NOT_RECORDED = 'Not recorded';
/** The Api's 400 when a new deployment arrives without a valid choice. The
 *  last sentence is for a form loaded before the choice existed. */
export const CHARGER_CONNECTIVITY_REQUIRED_MESSAGE = 'Choose how the chargers connect: Site WiFi or ethernet, EV Initiative cellular, or both. '
    + 'If the form does not show this choice, reload the page.';
/**
 * Who recorded a charger's connectivity. 'deployment': copied from the
 * registration form by commissioning. A later source (an operator edit, the
 * SIM provider) adds its own value here, and the deployment copy never
 * overwrites it.
 */
export const CHARGER_CONNECTIVITY_SOURCES = ['deployment'];
export function isChargerConnectivity(v) {
    return typeof v === 'string' && CHARGER_CONNECTIVITY.includes(v);
}
/** True when the stored choice includes that link: the link itself, or 'both'. */
export function connectivityIncludes(v, link) {
    return v === link || v === 'both';
}
/** The stored choice after ticking or unticking one link: '' when neither is ticked. */
export function connectivityWith(v, link, ticked) {
    const site = link === 'site_network' ? ticked : connectivityIncludes(v, 'site_network');
    const cellular = link === 'evi_cellular' ? ticked : connectivityIncludes(v, 'evi_cellular');
    return site && cellular ? 'both' : site ? 'site_network' : cellular ? 'evi_cellular' : '';
}
/** The display label, or 'Not recorded' for null, absent or unknown. */
export function chargerConnectivityLabel(v) {
    return isChargerConnectivity(v) ? CHARGER_CONNECTIVITY_LABELS[v] : CHARGER_CONNECTIVITY_NOT_RECORDED;
}
/**
 * The fields a unit takes from its deployment's choice, or null when there
 * is nothing to write.
 *
 *   unit null (commissioning is creating it): the deployment's choice, or
 *     null when the deployment has none (registered before the choice).
 *   unit given (commissioning is updating it): null when the deployment has
 *     no choice (never erases), when another source recorded the unit's
 *     connectivity (it wins), or when the unit already holds the same value
 *     (keeps its set_at). Otherwise the deployment's choice.
 */
export function connectivityFromDeployment(deploymentConnectivity, unit, at) {
    if (!isChargerConnectivity(deploymentConnectivity))
        return null;
    if (unit) {
        if (unit.connectivity_source != null && unit.connectivity_source !== 'deployment')
            return null;
        if (unit.connectivity === deploymentConnectivity)
            return null;
    }
    return { connectivity: deploymentConnectivity, connectivity_source: 'deployment', connectivity_set_at: at };
}
//# sourceMappingURL=chargerConnectivity.js.map